import store from '@/store'
import { queryMenu } from '@/http/modules/access'
import { PC_PLATFORM_ID } from '@/constants/globalConfig'
import { buildAccessModel, createViewResolver } from '@/utils/accessControl.mjs'
import { preparePageNavigationComponent } from '@/utils/pageNavigationRuntime.mjs'

const viewContext = require.context('../views', true, /\.vue$/, 'lazy')
const resolveView = createViewResolver(viewContext.keys().map((key) => ({
  key,
  loader: () => viewContext(key).then((module) => preparePageNavigationComponent(module.default || module)),
})))
const externalFrameComponent = () => import('../views/ExternalFrame.vue')
const missingViewComponent = () => import('../views/ResourceUnavailable.vue')

let initializationPromise = null
let initializingUserId = ''
let accessGeneration = 0
let routeRemovers = []

function removeDynamicRoutes() {
  routeRemovers.forEach((removeRoute) => removeRoute())
  routeRemovers = []
}

export function resetAccess() {
  accessGeneration += 1
  removeDynamicRoutes()
  initializationPromise = null
  initializingUserId = ''
  store.commit('access/reset')
}

if (typeof window !== 'undefined') {
  window.addEventListener('auth-session-cleared', resetAccess)
}

export async function ensureAccessInitialized(router, options = {}) {
  const userId = options.userId || ''
  const state = store.state.access
  if (state.initialized && (!userId || state.userId === userId) && !options.force) return state
  if (state.initialized && (options.force || (userId && state.userId !== userId))) resetAccess()
  if (state.error && !options.force) throw new Error(state.error)
  if (initializationPromise) {
    if (!userId || initializingUserId === userId) return initializationPromise
    resetAccess()
  }

  const generation = accessGeneration
  initializingUserId = userId
  let currentPromise
  currentPromise = (async () => {
    store.commit('access/setLoading', true)
    try {
      const response = await queryMenu({ platformIdFk: PC_PLATFORM_ID })
      if (generation !== accessGeneration) {
        throw new Error('权限初始化已取消')
      }
      if (response?.code !== 200) {
        throw new Error(response?.msg || '菜单权限加载失败')
      }

      const accessModel = buildAccessModel(response.data, {
        resolveView,
        externalFrameComponent,
        missingViewComponent,
      })
      removeDynamicRoutes()
      accessModel.routeRecords.forEach((route) => {
        routeRemovers.push(router.addRoute('home', route))
      })
      accessModel.errors.forEach((error) => {
        console.error('[Access Menu]', error.code, error.resourceId)
      })
      store.commit('access/setAccess', {
        menuList: accessModel.menuList,
        buttonPermissionList: accessModel.buttonPermissionList,
        userId,
      })
      return store.state.access
    } catch (error) {
      if (generation !== accessGeneration) throw error
      removeDynamicRoutes()
      const message = error?.message || '菜单权限加载失败'
      store.commit('access/setError', message)
      throw error
    } finally {
      if (initializationPromise === currentPromise) {
        initializationPromise = null
        initializingUserId = ''
      }
    }
  })()

  initializationPromise = currentPromise
  return currentPromise
}
